import toast from 'react-hot-toast';

import Request from '@/services/util/request-service';
import { QuizInfoRow } from '@/services/reports/reports-table-service';

import { ActiveQuizCookie, QuizAttemptData, QuizQuestionData, SavedFR, SavedMC } from 'quiz-types';
import { GroupAttemptsState, QuizAttempt } from 'course-types';

export type QuestionGradeStatus = 'correct' | 'incorrect' | 'ungraded';

const QuizUtilityService = {
  async retrieveQuiz(
    quizId: string,
    returnAttempt?: boolean,
  ): Promise<{
    quiz: Array<QuizQuestionData>;
    attempt?: Partial<QuizAttempt>;
    error?: string;
    blockingContent?: {
      name: string;
      id: string;
      type: string;
      requires_minimum_grade: boolean;
      minimum_grade_threshold: number;
    };
  }> {
    // Prevent invalid network calls when quizId is not a valid identifier
    if (!quizId || quizId === 'undefined' || quizId === 'show_all_quizzes') {
      return { quiz: [], attempt: undefined };
    }

    const quizData = await (await Request.get(`quiz/${quizId}`))?.json();
    if (quizData.error) return quizData;
    return {
      quiz: quizData.quiz.quiz_data || [],
      attempt: returnAttempt ? quizData?.attempt : undefined,
    };
  },

  buildQuizCookieFromAttempt(attempt: Partial<QuizAttempt>): ActiveQuizCookie {
    const answerData: Record<number, number | string> = {};
    const mcID = Object.keys(attempt?.mc_data ?? {});
    const frID = Object.keys(attempt?.fr_data ?? {});
    for (const mc of mcID) {
      answerData[Number(mc)] = (attempt?.mc_data ?? {})[Number(mc)]?.answerChosen;
    }
    for (const fr of frID) {
      answerData[Number(fr)] = (attempt?.fr_data ?? {})[Number(fr)]?.response;
    }

    return { start_time: attempt?.start_date ?? 'Error', data: answerData };
  },

  getQuestionAttemptInfo(
    attempt: QuizAttemptData | undefined,
    question: QuizQuestionData,
  ): SavedMC | SavedFR | undefined {
    if (question.type === 'Multiple Choice') {
      return (attempt?.mc_data ?? {})[question.index];
    }

    return (attempt?.fr_data ?? {})[question.index];
  },

  getQuestionGradeStatus(
    attempt: QuizAttemptData | undefined,
    question: QuizQuestionData,
  ): QuestionGradeStatus {
    const answer = this.getQuestionAttemptInfo(attempt, question);
    if (!answer) return 'ungraded';
    if (answer.gradedCorrect === true) return 'correct';
    if (answer.gradedCorrect === false) return 'incorrect';

    if (question.type === 'Multiple Choice') {
      return (answer as SavedMC).correct ? 'correct' : 'incorrect';
    }

    return 'ungraded';
  },

  getSubmittedAttempts(attempts: Array<QuizAttemptData> = []): Array<QuizAttemptData> {
    return attempts
      .filter((attempt) => {
        const submitDate = attempt?.submit_date ? new Date(attempt.submit_date) : null;
        return submitDate instanceof Date && !isNaN(submitDate.getTime());
      })
      .sort((a, b) => {
        return new Date(a.submit_date!).getTime() - new Date(b.submit_date!).getTime();
      });
  },

  async updateQuizAttempt(
    quizId: string,
    mc: Record<number, SavedMC>,
    fr: Record<number, SavedFR>,
    startDate: Date | string,
  ) {
    try {
      const body = {
        quizId: quizId,
        quizAnswers: { mc_data: mc, fr_data: fr },
        startDate: startDate,
      };
      const res = await (await Request.post('quiz/attempt/update', body))?.json();
      return res as { start_date: Date | string };
    } catch (e: any) {
      toast.error(e.message);
    }
  },

  // TODO: set type
  async submitQuiz(
    quizId: string,
    answers: Record<number, number | string>,
    courseId: string,
    startDate: Date | string,
    groupId: string,
  ) {
    try {
      const body = {
        courseId: courseId,
        quizId: quizId,
        quizAnswers: answers,
        startDate: startDate,
        groupId: groupId,
      };
      const res = await (await Request.post('quiz/attempt/submit', body))?.json();
      return res as { grade: any; MCData: Array<any>; FRData: Array<any>; submission: Date };
    } catch (e: any) {
      toast.error(e.message);
    }
  },

  async getGroupQuizAttempts(
    quizId: string,
    setGroupAttempts: React.Dispatch<React.SetStateAction<GroupAttemptsState>>,
    activeGroupData: UserGroup,
  ): Promise<void> {
    // if invalid group, return
    if (!activeGroupData?.id) {
      return;
    }
    const response = await Request.post('progress/group/quiz', {
      groupId: activeGroupData.id,
      quizId: [quizId],
    });
    const groupAttempts = await response?.json();
    const attemptsMap =
      groupAttempts?.groupAttemptsByQuiz?.[quizId] ?? groupAttempts?.groupAttempts ?? {};
    setGroupAttempts({ id: quizId, attempts: attemptsMap });
  },

  // TODO: Rename
  processQuizTableRows(
    attemptData: Record<string, Array<QuizAttempt>>,
    userList: Array<GroupListUserLimited>,
    strategy: 'first' | 'high' | 'last',
  ): QuizInfoRow[] {
    const rows: Array<QuizInfoRow> = [];

    for (const [email, attempts] of Object.entries(attemptData)) {
      const gradedAttempts = attempts.filter((attempt) => attempt.grade >= 0);
      const user = userList.find((u) => u.email == email);

      const rowEntry: QuizInfoRow = {
        first_name: user?.first_name ?? '',
        last_name: user?.last_name ?? '',
        email: email,
        correct: 'N/A',
        grade: 'Not Graded',
        completion: 'N/A',
      };
      if (gradedAttempts.length === 0) {
        rows.push(rowEntry);
      } else {
        let selectedAttempt: QuizAttempt;
        switch (strategy) {
          case 'first':
            selectedAttempt = this.getFirstAttempt(gradedAttempts);
            break;
          case 'last':
            selectedAttempt = this.getLastAttempt(gradedAttempts);
            break;
          case 'high':
          default:
            selectedAttempt = this.getHighestAttempt(gradedAttempts);
            break;
        }

        rowEntry.grade = selectedAttempt?.grade ?? 'Not Graded';
        rowEntry.completion = selectedAttempt ? new Date(selectedAttempt.submit_date) : 'N/A';

        const { correct, total } = this.processQuizAttempt(selectedAttempt);
        rowEntry.correct = selectedAttempt ? `${correct} / ${total}` : 'N/A';

        rows.push(rowEntry);
      }
    }

    return rows;
  },

  processQuizAttempt(quizAttempt: QuizAttempt) {
    let correct = 0;
    let total = 0;

    for (const [qIndex, qData] of Object.entries(quizAttempt?.mc_data ?? {})) {
      total++;
      if (qData.correct) {
        correct++;
      }
    }

    return { correct, total };
  },

  getHighestAttempt(quizAttempts: Array<QuizAttempt>) {
    const gradeReorder = [...quizAttempts].sort((a, b) => b.grade - a.grade);
    return gradeReorder.length > 0 ? gradeReorder[0] : ({} as QuizAttempt);
  },

  getFirstAttempt(quizAttempts: Array<QuizAttempt>): QuizAttempt {
    return quizAttempts.length > 0 ? quizAttempts[0] : ({} as QuizAttempt);
  },

  getLastAttempt(quizAttempts: Array<QuizAttempt>): QuizAttempt {
    return quizAttempts.length > 0 ? quizAttempts[quizAttempts.length - 1] : ({} as QuizAttempt);
  },
};

export default QuizUtilityService;
